import React from "react"

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid, IconButton, TextField,
    Typography
} from "@mui/material"

import {
    ErrorResponse, Loading,
    MatesSelect,
    MatesTextField, RestResponse,
    withUseFormHook,
} from "@sinossi/mates-react-library"

import {
    t,
    Trans,
} from "@lingui/macro"
import { UseFormReturn } from "react-hook-form"
import { Editor } from "react-draft-wysiwyg"
import {
    convertFromRaw,
    convertToRaw,
    EditorState,
} from "draft-js"

import {
    AssignableUsers,
    NewStepRequest,
    TemplateStep, WorkflowObjectType,
    WorkflowStepType
} from "models"

import NewStepValidation from "./form"
import { WorkflowService } from "../../../../services/workflow.service"
import { UseRouterParams } from "../../../../services/router"
import { MatesComponent, MatesComponentState } from "../../../mates-component"
import Icon from "@mdi/react"
import { mdiMenuClose, mdiMenuOpen } from "@mdi/js"
import { Placeholders } from "../../../commons/placeholders"
import { Autocomplete } from "@mui/lab"

interface OptionsType {
    id: string,
    name: string
}

interface NewStepProps extends UseFormReturn, UseRouterParams {
    openDialog: boolean
    onClose: () => void
    submit: (value: NewStepRequest) => void
    workflowType?: WorkflowObjectType
    stepData?: TemplateStep
    stepOrder?: number
    create?: boolean
    action?: boolean
}
interface NewStepState extends MatesComponentState {
    editorState: EditorState
    users: AssignableUsers[]
    loading: boolean
    openPlaceholders: boolean
    timeout: any
    userValue: any
    userInputValue: string
    userOptions: OptionsType[]
}
class NewStep extends React.Component<NewStepProps, NewStepState> {
    private readonly matesComponent: MatesComponent<NewStepProps, NewStepState> = new MatesComponent(this)

    private stepType: { key: string, value: any }[] = [
        { key: WorkflowStepType.NOTIFICATION, value: "Notifica" },
        { key: WorkflowStepType.EMAIL, value: "E-Mail" },
        { key: WorkflowStepType.PEC, value: "PEC" },
        { key: WorkflowStepType.PHONE_CALL, value: "Telefonata" },
        { key: WorkflowStepType.LETTER, value: "Lettera" },
        { key: WorkflowStepType.MANUAL_ACTION, value: "Azione Manuale" }
    ]

    constructor(props: NewStepProps) {
        super(props)
        this.state = {
            ...this.matesComponent.setupState(),
            editorState: EditorState.createEmpty(),
            users: [],
            loading: true,
            openPlaceholders: false,
            userValue: null,
            userInputValue: "",
            userOptions: [],
            timeout: null
        }
    }

    componentDidMount() {
        this.getAssignableUsers(true)

        if (this.props.action) {
            this.props.setValue("stepOrder", "1")
        }

        if (this.props.stepOrder) {
            this.props.setValue("stepOrder", this.props.stepOrder.toString())
        }

        if (this.props.create || this.props.stepData === undefined) {
            this.props.setValue("stepType", WorkflowStepType.NOTIFICATION)
            this.props.setValue("stepDeadline", 0)
            return
        }

        const contentState = convertFromRaw(this.props.stepData.content)
        this.props.setValue("stepName", this.props.stepData.name)
        this.props.setValue("stepType", this.props.stepData.actionType)
        this.props.setValue("stepOrder", this.props.stepData.order)
        this.props.setValue("stepDeadline", this.props.stepData.deadlineDays)
        this.setState(() => ({
            editorState: EditorState.createWithContent(contentState),
        }))
    }

    private getAssignableUsers = (init?: boolean) => {
        if (init) {
            WorkflowService.getAssignableUsers("-1", this.getAssignableUsersCallbackAndInit, this.matesComponent.onException)
        }
        WorkflowService.getAssignableUsers("-1", this.getAssignableUsersCallback, this.matesComponent.onException)
    }

    private getAssignableUsersCallback = (response: RestResponse<AssignableUsers[], ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            users: response.data ?? [],
            loading: false
        }))
    }

    private getAssignableUsersCallbackAndInit = (response: RestResponse<AssignableUsers[], ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            users: response.data ?? [],
            loading: false
        }), () => this.setUserOptions(this.state.users, true))
    }

    private submit = (values: any) => {
        const editorValue = convertToRaw(this.state.editorState.getCurrentContent())

        let request: NewStepRequest = {
            name: values.stepName,
            actionType: values.stepType,
            order: values.stepOrder,
            deadlineDays: values.stepDeadline,
            executor: values.stepAssignTo === "" ? null : values.stepAssignTo,
            content: editorValue
        }

        this.props.submit(request)
    }

    private handleEditorChange = (editorState: EditorState) => {
        this.setState(() => ({
            editorState: editorState,
        }))
    }

    private openPlaceholders = () => {
        this.setState(() => ({
            openPlaceholders: true,
        }))
    }

    private closePlaceholders = (value?: any) => {
        this.setState(() => ({
            openPlaceholders: false,
        }))
    }


    private fetchUsers = (input: string) => {
        if (this.state.timeout) {
            clearTimeout(this.state.timeout)
        }
        this.setState(() => ({
            timeout: setTimeout(() => this.filterUsers(input), 1000)
        }))
    }

    private filterUsers = (input: string) => {
        let newList: AssignableUsers[] = []

        this.state.users.map((user) => {
            let userName = (user.accountDetails.firstName ?? "") + (user.accountDetails.lastName ? " " + user.accountDetails.lastName : "") + " - " + user.username
            if (userName.includes(input)) {
                newList.push(user)
            }
        })

        this.setUserOptions(newList)
    }

    private setUserOptions = (newList: AssignableUsers[], init?: boolean) => {
        let userOptions: OptionsType[] = []

        newList.map((user) => userOptions.push(
            {
                id: user.id,
                name: (user.accountDetails.firstName ?? "") + (user.accountDetails.lastName ? " " + user.accountDetails.lastName + " - " : "") + user.username
            }
        ))

        if (init) {
            this.setState(() => ({
                userOptions: userOptions
            }), this.setUserValue)
        }
        else {
            this.setState(() => ({
                userOptions: userOptions
            }))
        }

    }

    private setUserValue = () => {
        if (this.props.stepData && this.props.stepData.defaultExecutor && this.props.stepData.defaultExecutor.accountDetails) {
            let userValue = this.state.userOptions.find((user) => {
                if (user.id === this.props.stepData!.defaultExecutor!.id) {
                    return user
                }
            })

            this.setState(() => ({
                userValue: userValue !== undefined ? userValue : null,
                userOptions: []
            }))

            this.props.setValue("stepAssignTo", this.props.stepData.defaultExecutor.accountDetails.id)

        }
    }

    render() {
        let { userValue, userOptions } = this.state

        return (
            <>
                <Loading show={this.state.loading} />
                {
                    !this.state.loading &&
                    <Dialog open={this.props.openDialog} onClose={this.props.onClose} maxWidth={"lg"}>
                        <Box sx={{ m: 2 }}>
                            <Typography variant="h5">
                                {
                                    this.props.create ?
                                        <Trans id={'workflow.template.new-step.title'}>
                                            Crea {this.props.action ? t({ id: "new-step.new-step", message: "nuovo step" }) : t({ id: "new-step.new-action", message: "nuova azione" })}
                                        </Trans>
                                        :
                                        <Trans id={'workflow.template.update-step.title'}>
                                            Modifica {this.props.action ? t({ id: "new-step.edit-step", message: "step" }) : t({ id: "new-step.edit-action", message: "azione" })}
                                        </Trans>
                                }
                            </Typography>
                            <form onSubmit={this.props.handleSubmit(this.submit)}>
                                <DialogContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={this.state.openPlaceholders ? 3 : 4}>
                                            <MatesTextField variant={'outlined'}
                                                label={t({
                                                    id: "workflow.template.new-step.name",
                                                    message: "Nome",
                                                })}
                                                name="stepName"
                                                control={this.props.control}
                                                trigger={this.props.trigger}
                                                formState={this.props.formState}
                                                sx={{ mb: 3 }}
                                            />
                                            <MatesSelect variant={"outlined"}
                                                label={t({
                                                    id: "workflow.template.new-step.type",
                                                    message: "Tipo",
                                                }
                                                )}
                                                name="stepType"
                                                control={this.props.control}
                                                trigger={this.props.trigger}
                                                formState={this.props.formState}
                                                sx={{ mb: 3 }}>
                                                {
                                                    this.stepType.map((option) => (
                                                        <option value={option.key} key={option.key}>{option.value}</option>
                                                    ))
                                                }
                                            </MatesSelect>

                                            <MatesTextField variant={'outlined'}
                                                label={t({
                                                    id: "workflow.template.new-step.order",
                                                    message: "Ordine",
                                                })}
                                                name="stepOrder"
                                                disabled={this.props.stepOrder !== null}
                                                control={this.props.control}
                                                trigger={this.props.trigger}
                                                formState={this.props.formState}
                                                sx={{ mb: 3 }}
                                            />

                                            <MatesTextField variant={'outlined'}
                                                label={t({
                                                    id: "workflow.template.new-step.deadline",
                                                    message: "Scadenza (in giorni)",
                                                })}
                                                name="stepDeadline"
                                                control={this.props.control}
                                                trigger={this.props.trigger}
                                                formState={this.props.formState}
                                                sx={{ mb: 3 }}
                                            />
                                            <Autocomplete
                                                getOptionLabel={(option) => option.name}
                                                filterOptions={(x) => x}
                                                options={userOptions}
                                                autoComplete
                                                includeInputInList
                                                filterSelectedOptions
                                                value={userValue}
                                                noOptionsText={t({ id: "create-contract-modal.no-user", message: "Inizia a digitare per avviare la ricerca" })}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        this.setState(() => ({
                                                            userValue: newValue, userOptions: [newValue, ...userOptions]
                                                        }));
                                                        this.props.setValue("stepAssignTo", newValue.id)
                                                    }
                                                }}
                                                onInputChange={(event, newInputValue) => {
                                                    this.setState({ userInputValue: newInputValue }, () => {
                                                        if (this.state.userInputValue === '') {
                                                            this.setState({ userOptions: userValue ? [userValue] : [] });
                                                        }
                                                        else {
                                                            this.fetchUsers(this.state.userInputValue)
                                                        }
                                                    })
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params}
                                                        label={t({ id: "create-contract-modal.select-user", message: "Assegna a..." })}
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={this.props.workflowType ? (this.state.openPlaceholders ? 4 : 7) : 8}>
                                            <Editor
                                                editorState={this.state.editorState}
                                                onEditorStateChange={this.handleEditorChange}

                                                toolbar={{
                                                    options: ['inline', 'list', 'link'],
                                                    inline: {
                                                        options: ['bold', 'italic'],
                                                    },

                                                    list: {
                                                        options: ['unordered', 'ordered'],
                                                    },
                                                    link: {
                                                        options: ['link'],
                                                    },
                                                }}
                                                toolbarStyle={{
                                                    backgroundColor: "#11347B",
                                                    borderColor: "#11347B"
                                                }}
                                                editorStyle={{
                                                    minHeight: "250px",
                                                    border: "1px solid lightgray"
                                                }}
                                            />
                                        </Grid>
                                        {
                                            this.props.workflowType &&
                                            <>
                                                <Grid item xs={1} textAlign={"right"}>
                                                    <IconButton
                                                        aria-controls="menu"
                                                        aria-haspopup="true"
                                                        onClick={this.state.openPlaceholders ? () => this.closePlaceholders() : () => this.openPlaceholders()}
                                                        color="inherit"
                                                    >
                                                        {
                                                            this.state.openPlaceholders &&
                                                            <Icon path={mdiMenuOpen} size={1} />
                                                        }
                                                        {
                                                            !this.state.openPlaceholders &&
                                                            <Icon path={mdiMenuClose} size={1} />
                                                        }
                                                    </IconButton>
                                                </Grid>
                                                {
                                                    this.state.openPlaceholders &&
                                                    <Placeholders templateType={this.props.workflowType} />
                                                }
                                            </>
                                        }

                                    </Grid>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={this.props.onClose}
                                        variant={"contained"}
                                        color="neutral"
                                        sx={{
                                            textTransform: "none",
                                            mr: 1,
                                            width: 100
                                        }}
                                        disableElevation>
                                        <Trans id={"workflow.template.new-template.abort"}>
                                            Annulla
                                        </Trans>
                                    </Button>
                                    <Button type="submit"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: 'secondary.main',
                                            color: 'white',
                                            mr: 1,
                                            textTransform: "none",
                                            width: 100
                                        }}
                                    >
                                        <Trans id={"workflow.template.new-step.save"}>
                                            Salva
                                        </Trans>
                                    </Button>
                                </DialogActions>
                            </form>
                        </Box>

                    </Dialog>
                }
            </>
        )
    }
}

export default withUseFormHook<{}>(
    NewStep,
    NewStepValidation,
    {}
)
